import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { Link } from 'react-router-dom';
import { cn } from '../utils/cn';
import { servicesApi } from '../services/api';

// 服务接口（与API返回字段对齐）
interface Service {
  id: number;
  slug: string;
  name_zh: string;
  name_vi: string;
  icon_class: string;
  description_zh: string;
  description_vi: string;
  details_zh: string;
  details_vi: string;
  image_url: string;
  sort_order: number;
  is_active: number;
}

const ServicesPage: React.FC = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  // 从API获取服务数据
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await servicesApi.getAll();
        if (res.success && res.data) {
          setServices(res.data);
        }
      } catch (error) {
        console.error('获取服务数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // 解析details JSON
  const parseDetails = (detailsJson: string): string[] => {
    try {
      return JSON.parse(detailsJson || '[]');
    } catch {
      return [];
    }
  };

  // 图标映射
  const iconMap: Record<string, string> = {
    bolt: '⚡',
    thermostat: '🌡️',
    wind: '💨',
    building: '🏗️',
    layers: '🧱'
  };

  // 转换为渲染格式
  const formatServices = services.map(service => ({
    id: service.id,
    slug: service.slug,
    category: {
      zh: service.name_zh,
      vi: service.name_vi
    },
    icon: iconMap[service.icon_class] || '🔧',
    description: {
      zh: service.description_zh,
      vi: service.description_vi
    },
    features: parseDetails(language === 'zh' ? service.details_zh : service.details_vi).map((text: string) => ({
      zh: text,
      vi: text
    })),
    image_url: service.image_url
  }));

  const workflow = [
    {
      step: 1,
      title: {
        zh: '需求分析',
        vi: 'Phân tích nhu cầu'
      },
      description: {
        zh: '深入了解客户需求，分析项目特点，制定初步方案',
        vi: 'Tìm hiểu sâu nhu cầu khách hàng, phân tích đặc điểm dự án, xây dựng phương án sơ bộ'
      }
    },
    {
      step: 2,
      title: {
        zh: '方案设计',
        vi: 'Thiết kế phương án'
      },
      description: {
        zh: '专业团队进行详细设计，提供技术图纸和施工方案',
        vi: 'Đội ngũ chuyên nghiệp thiết kế chi tiết, cung cấp bản vẽ kỹ thuật và phương án thi công'
      }
    },
    {
      step: 3,
      title: {
        zh: '施工准备',
        vi: 'Chuẩn bị thi công'
      },
      description: {
        zh: '材料采购、设备调配、施工团队组建，确保资源到位',
        vi: 'Mua sắm vật liệu, điều phối thiết bị, thành lập đội thi công, đảm bảo nguồn lực sẵn sàng'
      }
    },
    {
      step: 4,
      title: {
        zh: '现场施工',
        vi: 'Thi công hiện trường'
      },
      description: {
        zh: '严格按照施工方案进行作业，确保质量和安全',
        vi: 'Thực hiện công việc theo phương án thi công nghiêm ngặt, đảm bảo chất lượng và an toàn'
      }
    },
    {
      step: 5,
      title: {
        zh: '质量验收',
        vi: 'Nghiệm thu chất lượng'
      },
      description: {
        zh: '完成工程后进行严格质量检查，确保符合标准',
        vi: 'Tiến hành kiểm tra chất lượng nghiêm ngặt sau khi hoàn thành công trình, đảm bảo đáp ứng tiêu chuẩn'
      }
    },
    {
      step: 6,
      title: {
        zh: '售后服务',
        vi: 'Dịch vụ sau bán hàng'
      },
      description: {
        zh: '提供长期维护服务和技术支持，保障客户利益',
        vi: 'Cung cấp dịch vụ bảo trì dài hạn và hỗ trợ kỹ thuật, bảo vệ lợi ích khách hàng'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
              {language === 'zh' ? '业务范围' : 'Dịch vụ'}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-blue-100 leading-relaxed">
              {language === 'zh'
                ? '提供工业厂房全方位的建筑工程服务，涵盖电气、暖通空调、通风、装饰改造、防水地坪漆等专业领域'
                : 'Cung cấp dịch vụ xây dựng công trình toàn diện cho nhà xưởng công nghiệp, bao gồm các lĩnh vực chuyên môn như điện, HVAC, thông gió, cải tạo trang trí, sơn sàn chống thấm'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
              {language === 'zh' ? '专业服务' : 'Dịch vụ chuyên nghiệp'}
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
              {language === 'zh'
                ? '我们提供涵盖工业厂房建设各个阶段的专业服务，确保项目高质量完成'
                : 'Chúng tôi cung cấp dịch vụ chuyên nghiệp bao phủ mọi giai đoạn xây dựng nhà xưởng công nghiệp, đảm bảo dự án hoàn thành với chất lượng cao'
              }
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {formatServices.map((service, index) => (
              <div
                key={index}
                className={cn(
                  "bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300",
                  "transform hover:-translate-y-1"
                )}
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="text-4xl">{service.icon}</div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {service.category[language]}
                      </h3>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description[language]}
                  </p>

                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">
                      {language === 'zh' ? '主要服务内容：' : 'Nội dung dịch vụ chính:'}
                    </h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 mt-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-700">{feature[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Link
                  to={`/services/${service.id}`}
                  className="bg-blue-50 px-6 py-4 block hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700 font-medium">
                      {language === 'zh' ? '了解更多' : 'Tìm hiểu thêm'}
                    </span>
                    <span className="text-blue-500">→</span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
              {language === 'zh' ? '服务流程' : 'Quy trình dịch vụ'}
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
              {language === 'zh'
                ? '我们遵循标准化的服务流程，确保每个项目从开始到结束都得到专业管理和质量控制'
                : 'Chúng tôi tuân theo quy trình dịch vụ tiêu chuẩn hóa, đảm bảo mỗi dự án từ đầu đến cuối đều được quản lý chuyên nghiệp và kiểm soát chất lượng'
              }
            </p>
          </div>

          <div className="space-y-8 md:space-y-12">
              {workflow.map((step, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 md:gap-6"
                >
                  {/* Step number badge */}
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-blue-600 rounded-full flex items-center justify-center z-10 shadow-lg">
                    <span className="text-white font-bold text-sm md:text-lg">{step.step}</span>
                  </div>

                  {/* Content - alternating on desktop */}
                  <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-sm">
                    <h3 className="text-base md:text-xl font-semibold text-gray-900 mb-2 md:mb-3">
                      {step.title[language]}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {step.description[language]}
                    </p>
                  </div>
                </div>
              ))}
            </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
                {language === 'zh' ? '为什么选择我们' : 'Tại sao chọn chúng tôi'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-gray-700">
                        {language === 'zh' ? '本地化专业团队，熟悉越南建筑法规' : 'Đội ngũ chuyên gia nội địa, quen thuộc quy định xây dựng Việt Nam'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-gray-700">
                        {language === 'zh' ? '中越双语服务，沟通无障碍' : 'Dịch vụ song ngữ Trung-Việt, giao tiếp thuận lợi'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-gray-700">
                        {language === 'zh' ? '工业厂房专业经验丰富' : 'Kinh nghiệm chuyên sâu về nhà xưởng công nghiệp'}
                      </span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-gray-700">
                        {language === 'zh' ? '一站式工程服务，省心省力' : 'Dịch vụ công trình trọn gói, tiết kiệm thời gian công sức'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-gray-700">
                        {language === 'zh' ? '严格质量控制，确保工程安全' : 'Kiểm soát chất lượng nghiêm ngặt, đảm bảo an toàn công trình'}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        ✓
                      </div>
                      <span className="text-gray-700">
                        {language === 'zh' ? '长期维护服务，保障客户利益' : 'Dịch vụ bảo trì dài hạn, bảo vệ lợi ích khách hàng'}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;